/**
 * Auth Integration Helper
 * Connects AuthService with GlobalStateManager
 * Architecture: Ensures JWT tokens are synchronized with GlobalStateManager
 * 
 * Note: GlobalStateManager now handles all LLM initialization internally
 */

import { authService } from "@carousel-office/shared";
import GlobalStateManager from "./global-state/globalStateManager";

/**
 * Initialize auth integration
 * Sets up listeners to sync auth state with GlobalStateManager
 * Call this once at application startup, AFTER GlobalStateManager.initialize()
 */
export async function initializeAuthIntegration(): Promise<void> {
    console.log('[AuthIntegration] Initializing auth integration...');
    
    const globalState = GlobalStateManager.getInstance();
    
    // Get current auth state and update GlobalStateManager
    let currentToken = authService.getAccessToken();
    
    // If token is null but user is still authenticated, try to refresh
    if (!currentToken && authService.isAuthenticated()) {
        console.log('[AuthIntegration] Token expired, attempting refresh before initialization...');
        try {
            currentToken = await authService.refreshAccessToken();
        } catch (error) {
            console.error('[AuthIntegration] Token refresh failed:', error);
        }
    }
    
    if (currentToken) {
        await globalState.setJwtToken(currentToken);
        console.log('[AuthIntegration] Initialized with existing auth token');
    }
    
    // Listen for auth changes
    authService.onAuthChange(async (isAuthenticated, user) => {
        console.log('[AuthIntegration] Auth state changed:', {
            isAuthenticated,
            userEmail: user?.email
        });
        
        if (isAuthenticated) {
            let token = authService.getAccessToken();
            
            // If authenticated but token is null (expired), try to refresh
            if (!token) {
                console.log('[AuthIntegration] Token expired during auth change, attempting refresh...');
                try {
                    token = await authService.refreshAccessToken();
                } catch (error) {
                    console.error('[AuthIntegration] Token refresh failed:', error);
                    return; // Don't update GlobalStateManager if refresh failed
                }
            }
            
            if (token) {
                // GlobalStateManager will update all adapters automatically
                await globalState.setJwtToken(token);
                console.log('[AuthIntegration] Updated JWT token');
            }
        } else {
            // Clear token on logout
            await globalState.setJwtToken(null);
            console.log('[AuthIntegration] Cleared JWT token');
        }
    });
    
    console.log('[AuthIntegration] Auth integration initialized');
}

/**
 * Get current JWT token from AuthService
 */
export function getCurrentJwtToken(): string | null {
    return authService.getAccessToken();
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
    return authService.isAuthenticated();
}

// Re-export authService for tools that need direct access (e.g., agentContext.ts)
export { authService };

